import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
} from '@mui/material';
// Removed unused Timeline imports
import {
  Psychology as ThinkIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Lightbulb as IdeaIcon,
  AutoAwesome as InsightIcon,
} from '@mui/icons-material';
import { AbstractThinkerService } from '../services/abstractThinkerService';
import {
  AbstractThinkingSession,
  AbstractThinkingSessionStorage,
  AbstractThinkingRequest,
  AgentTurn,
  THINKING_CATEGORIES,
  ITERATION_PRESETS,
} from '../models/abstractThinker';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`abstract-thinker-tabpanel-${index}`}
      aria-labelledby={`abstract-thinker-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

const AbstractThinker: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [tabValue, setTabValue] = useState(0);
  const [topic, setTopic] = useState('');
  const [category, setCategory] = useState('');
  const [iterations, setIterations] = useState(5);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSession, setCurrentSession] = useState<AbstractThinkingSession | null>(null);
  const [sessions, setSessions] = useState<AbstractThinkingSession[]>([]);
  const [expandedTurn, setExpandedTurn] = useState<number | null>(null);

  // Load sessions from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('abstractThinkingSessions');
    if (stored) {
      const data: AbstractThinkingSessionStorage = JSON.parse(stored);
      setSessions(data.sessions.map(s => ({
        ...s,
        createdAt: new Date(s.createdAt)
      })));
    }
  }, []);

  // Save sessions to localStorage
  const saveSessions = (newSessions: AbstractThinkingSession[]) => {
    const storage: AbstractThinkingSessionStorage = {
      sessions: newSessions,
      lastUpdated: new Date()
    };
    localStorage.setItem('abstractThinkingSessions', JSON.stringify(storage));
    setSessions(newSessions);
  };

  const handleStartThinking = async () => {
    if (!topic.trim()) {
      setError('Please provide a topic or question to think about');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request: AbstractThinkingRequest = {
        initialQueryOrTopic: category ? `[${category}] ${topic.trim()}` : topic.trim(),
        iterations,
        maxTokensPerAgentTurn: maxTokens
      };

      const response = await AbstractThinkerService.startThinking(request);
      
      if (response.finalConclusion || response.thinkingProcess) {
        const newSession: AbstractThinkingSession = {
          id: Date.now().toString(),
          topic: topic.trim(),
          iterations: response.totalIterations || iterations,
          finalConclusion: response.finalConclusion,
          thinkingProcess: response.thinkingProcess,
          createdAt: new Date()
        };
        
        setCurrentSession(newSession);
        saveSessions([newSession, ...sessions]);
        setTabValue(1); // Switch to results tab
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start abstract thinking session');
    } finally {
      setLoading(false);
    }
  };

  const handleExportSession = (session: AbstractThinkingSession) => {
    let content = `# Abstract Thinking Session\n\n`;
    content += `**Topic:** ${session.topic}\n`;
    content += `**Date:** ${session.createdAt.toLocaleDateString()}\n`;
    content += `**Iterations:** ${session.iterations}\n\n`;
    
    if (session.thinkingProcess && session.thinkingProcess.length > 0) {
      content += `## Thinking Process\n\n`;
      session.thinkingProcess.forEach((turn, index) => {
        content += `### Step ${index + 1}`;
        if (turn.agentName) content += ` - ${turn.agentName}`;
        content += '\n\n';
        
        if (turn.input) {
          content += `**Input:** ${turn.input}\n\n`;
        }
        
        if (turn.output) {
          content += `**Output:**\n${turn.output}\n\n`;
        }
        
        if (turn.timestamp) {
          content += `*Timestamp: ${new Date(turn.timestamp).toLocaleString()}*\n\n`;
        }
        
        content += '---\n\n';
      });
    }
    
    if (session.finalConclusion) {
      content += `## Final Conclusion\n\n${session.finalConclusion}\n`;
    }

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `abstract-thinking-${session.topic.replace(/\s+/g, '-').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyConclusion = (conclusion: string) => {
    navigator.clipboard.writeText(conclusion);
  };

  const handleCopyTurn = (turn: AgentTurn) => {
    const text = `${turn.agentName || 'Agent'}:\n${turn.output || turn.input || ''}`;
    navigator.clipboard.writeText(text);
  };

  const handleLoadSession = (session: AbstractThinkingSession) => {
    setCurrentSession(session);
    setTopic(session.topic);
    setIterations(session.iterations);
    setTabValue(1); // Switch to results tab
  };

  const handleDeleteSession = (sessionId: string) => {
    saveSessions(sessions.filter(s => s.id !== sessionId));
  };

  const handleReset = () => {
    setTopic('');
    setCategory('');
    setIterations(5);
    setMaxTokens(2048);
    setCurrentSession(null);
    setExpandedTurn(null);
    setTabValue(0);
  };

  const getAgentIcon = (agentName?: string) => {
    if (!agentName) return <ThinkIcon />;
    if (agentName.toLowerCase().includes('insight')) return <InsightIcon />;
    if (agentName.toLowerCase().includes('idea')) return <IdeaIcon />;
    return <ThinkIcon />;
  };

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mt: { xs: 2, sm: 3 } }}>
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'start', sm: 'center' }, 
            mb: { xs: 2, sm: 3 },
            gap: { xs: 1, sm: 0 }
          }}
        >
          <ThinkIcon 
            sx={{ 
              mr: { xs: 0, sm: 2 }, 
              fontSize: { xs: 32, sm: 40 }, 
              color: 'primary.main',
              mb: { xs: 1, sm: 0 }
            }} 
          />
          <Box>
            <Typography 
              variant="h4" 
              component="h1"
              sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' } }}
            >
              Abstract Thinker
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              {isMobile ? 'Explore concepts through AI reasoning' : 'Explore complex concepts through iterative AI reasoning and philosophical inquiry'}
            </Typography>
          </Box>
        </Box>

        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          variant={isMobile ? 'scrollable' : 'standard'}
          {...(isMobile && { scrollButtons: 'auto' })}
          sx={{
            '& .MuiTab-root': {
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              minHeight: { xs: 44, sm: 48 }
            }
          }}
        >
          <Tab label={isMobile ? "New" : "New Session"} />
          <Tab label="Results" disabled={!currentSession} />
          <Tab label="History" icon={<HistoryIcon fontSize={isMobile ? 'small' : 'medium'} />} iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Topic or Question"
              placeholder={isMobile ? "Enter a topic to explore..." : "Enter a philosophical question, abstract concept, or complex problem to explore..."}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              helperText={isMobile ? undefined : "The AI will explore this topic through multiple rounds of abstract reasoning"}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                {isMobile ? "Category" : "Thinking Category (Optional)"}
              </InputLabel>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                label={isMobile ? "Category" : "Thinking Category (Optional)"}
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  None
                </MenuItem>
                {THINKING_CATEGORIES.map((cat) => (
                  <MenuItem 
                    key={cat} 
                    value={cat}
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography 
                gutterBottom
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                Iteration Depth
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, mb: 2 }}>
                {ITERATION_PRESETS.map((preset) => (
                  <Button
                    key={preset.value}
                    variant={iterations === preset.value ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setIterations(preset.value)}
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      minHeight: 44,
                      px: { xs: 1.5, sm: 2 }
                    }}
                  >
                    {preset.label}
                  </Button>
                ))}
              </Box>
              <Slider
                value={iterations}
                onChange={(_, value) => setIterations(value as number)}
                min={1}
                max={10}
                marks
                valueLabelDisplay="auto"
                sx={{
                  '& .MuiSlider-markLabel': {
                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                  }
                }}
              />
            </Box>

            <Box>
              <Typography 
                gutterBottom
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                Max Tokens per Turn: {maxTokens}
              </Typography>
              <Slider
                value={maxTokens}
                onChange={(_, value) => setMaxTokens(value as number)}
                min={512}
                max={8192}
                step={512}
                marks={[
                  { value: 512, label: '512' },
                  { value: 2048, label: '2048' },
                  { value: 4096, label: '4096' },
                  { value: 8192, label: '8192' }
                ]}
                valueLabelDisplay="auto"
                sx={{
                  '& .MuiSlider-markLabel': {
                    fontSize: { xs: '0.65rem', sm: '0.75rem' }
                  }
                }}
              />
            </Box>

            {error && (
              <Alert 
                severity="error" 
                onClose={() => setError(null)}
                sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
              >
                {error}
              </Alert>
            )}

            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 2 } 
              }}
            >
              <Button
                variant="contained"
                onClick={handleStartThinking}
                disabled={loading || !topic.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : !isMobile && <ThinkIcon />}
                size="large"
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  minHeight: 44,
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                {loading ? 'Thinking...' : (isMobile ? 'Start' : 'Start Abstract Thinking')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={loading}
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  minHeight: 44,
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                Reset
              </Button>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {currentSession && (
            <Box>
              <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant="h5" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  {currentSession.topic}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                >
                  {currentSession.iterations} iterations • {currentSession.createdAt.toLocaleDateString()}
                </Typography>
              </Box>

              {currentSession.finalConclusion && (
                <Card sx={{ mb: { xs: 2, sm: 3 }, backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                      >
                        Final Conclusion
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => handleCopyConclusion(currentSession.finalConclusion!)}
                        sx={{ 
                          color: 'primary.contrastText',
                          minWidth: 44,
                          minHeight: 44
                        }}
                      >
                        <CopyIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </Box>
                    <Typography 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}
                    >
                      {currentSession.finalConclusion}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {currentSession.thinkingProcess && currentSession.thinkingProcess.length > 0 && (
                <Box>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      mb: 2,
                      fontSize: { xs: '1.125rem', sm: '1.25rem' }
                    }}
                  >
                    Thinking Process
                  </Typography>
                  
                  {currentSession.thinkingProcess.map((turn, index) => (
                    <Accordion
                      key={index}
                      expanded={expandedTurn === index}
                      onChange={() => setExpandedTurn(expandedTurn === index ? null : index)}
                      sx={{ mb: 1 }}
                    >
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon fontSize={isMobile ? 'small' : 'medium'} />}
                        sx={{ 
                          minHeight: { xs: 44, sm: 48 },
                          '& .MuiAccordionSummary-content': {
                            my: { xs: 1, sm: 1.5 }
                          }
                        }}
                      >
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'start', sm: 'center' }, 
                            gap: { xs: 1, sm: 2 }, 
                            width: '100%' 
                          }}
                        >
                          {getAgentIcon(turn.agentName)}
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              Step {index + 1}: {turn.agentName || 'Agent Turn'}
                            </Typography>
                            {turn.input && (
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {turn.input.substring(0, isMobile ? 50 : 100)}...
                              </Typography>
                            )}
                          </Box>
                          {turn.timestamp && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            >
                              {new Date(turn.timestamp).toLocaleTimeString()}
                            </Typography>
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: { xs: 2, sm: 3 } }}>
                        {turn.input && (
                          <Box sx={{ mb: 2 }}>
                            <Typography 
                              variant="subtitle2" 
                              color="primary" 
                              gutterBottom
                              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                            >
                              Input:
                            </Typography>
                            <Typography 
                              sx={{ 
                                whiteSpace: 'pre-wrap', 
                                fontSize: { xs: '0.8125rem', sm: '0.95rem' }
                              }}
                            >
                              {turn.input}
                            </Typography>
                          </Box>
                        )}
                        {turn.output && (
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography 
                                variant="subtitle2" 
                                color="primary" 
                                gutterBottom
                                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                              >
                                Output:
                              </Typography>
                              <IconButton 
                                size="small" 
                                onClick={() => handleCopyTurn(turn)}
                                sx={{ minWidth: 44, minHeight: 44 }}
                              >
                                <CopyIcon fontSize={isMobile ? 'small' : 'medium'} />
                              </IconButton>
                            </Box>
                            <Typography 
                              sx={{ 
                                whiteSpace: 'pre-wrap', 
                                fontSize: { xs: '0.8125rem', sm: '0.95rem' }
                              }}
                            >
                              {turn.output}
                            </Typography>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}

              <Box 
                sx={{ 
                  mt: { xs: 2, sm: 3 }, 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 2 } 
                }}
              >
                <Button
                  variant="contained"
                  startIcon={!isMobile && <DownloadIcon />}
                  onClick={() => handleExportSession(currentSession)}
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minHeight: 44,
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  {isMobile ? 'Export' : 'Export Session'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minHeight: 44,
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  New Session
                </Button>
              </Box>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {sessions.length > 0 ? (
            <List sx={{ px: { xs: 0, sm: 0 } }}>
              {sessions.map((session) => (
                <ListItem 
                  key={session.id} 
                  divider
                  sx={{ 
                    cursor: 'pointer',
                    py: { xs: 2, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'start', sm: 'center' },
                    gap: { xs: 1, sm: 0 }
                  }}
                  onClick={() => handleLoadSession(session)}
                >
                  <ListItemText
                    primary={session.topic}
                    secondary={
                      <>
                        {session.iterations} iterations • 
                        {session.thinkingProcess?.length || 0} steps • 
                        {session.createdAt.toLocaleDateString()}
                      </>
                    }
                    primaryTypographyProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    secondaryTypographyProps={{
                      sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                    }}
                    sx={{ flex: 1, pr: { xs: 0, sm: 2 } }}
                  />
                  <ListItemSecondaryAction
                    sx={{
                      position: { xs: 'relative', sm: 'absolute' },
                      right: { xs: 'auto', sm: 16 },
                      transform: { xs: 'none', sm: 'translateY(-50%)' },
                      display: 'flex',
                      gap: 1,
                      width: { xs: '100%', sm: 'auto' },
                      justifyContent: { xs: 'flex-end', sm: 'flex-start' }
                    }}
                  >
                    <Tooltip title="Export">
                      <IconButton 
                        edge="end" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportSession(session);
                        }}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DownloadIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        edge="end" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSession(session.id);
                        }}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
              <ThinkIcon 
                sx={{ 
                  fontSize: { xs: 48, sm: 60 }, 
                  color: 'text.secondary', 
                  mb: 2 
                }} 
              />
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
              >
                No thinking sessions yet
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                }}
              >
                Your abstract thinking explorations will appear here
              </Typography>
            </Box>
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default AbstractThinker;